import { useInfiniteQuery, useMutation, UseMutationOptions, useQuery, UseQueryOptions } from '@tanstack/react-query'
import qs from 'qs'
import { useMemo } from 'react'
import { APPROVALS_LIST_QUERY_KEY } from '~/api/approvals'
import { APPROVERS_ALL_KEY, WorkflowApproverResponse } from '~/api/approvers'
import type { CandidateWorkerDetails } from '~/api/candidate'
import { Comment, CommentFormData } from '~/api/comments'
import { OffsetLimit, OffsetLimitPaginationResponse } from '~/api/common'
import { ShiftDifferential } from '~/api/shifts'
import { GuidedRateScheduleData } from '~/api/guidedRateSchedules'
import {
  BudgetHistoryRecord,
  ChargeCodesConfigValues,
  ContractField,
  ShiftDetails,
  ShiftStrategyRecord,
  WorkOrderActionDirection,
  WorkOrderApprovalFieldSet,
  WorkOrderAttachment,
  WorkOrderBudgetAllocations,
  WorkOrderCancelRequest,
  WorkOrderConfigResponse,
  WorkOrderCurrentData,
  WorkOrderDatedDetails,
  WorkOrderDetail,
  WorkOrderEffectiveDatedFields,
  WorkOrderEndRequest,
  WorkOrderGeneralConfigResponse,
  WorkOrderOnboardResult,
  WorkOrderRevisionRequest,
  WorkOrderRevisionsData,
  WorkOrderRevisionSummary,
  WorkOrderWorkdayData,
  WorkTypeForProfileRecord,
  WorkTypeRecord
} from '~/api/work_order/types'
import { ChargeCodeRawValues } from '~/charge_codes/types'
import fetch from '~/common/fetch'
import { ifDefinedElse } from '~/common/objectUtils'
import { queryClient } from '~/common/react-query/queryClient'
import { DateString, PrimaryKey } from '~/common/types'
import { QueryParams } from '~/components/v2/ResultsLayout/ListQueryProvider/ListQueryProvider'
import { Log } from '~/settings/settingsApi'
import { closestEdfOn } from '~/work_order/modify_work_order/utils'
import { WorkOrder } from '~/work_order_list/api'
import { actionBarDef, filtersDef } from '~/work_order_list/filtersConfig'
import { WorkOrderNestedData, WorkOrderNestedDataArray } from '.'
import { invalidateJobApplicationQueries, JobApplicationOfferPayload } from '../jobApplications'

export const WORK_ORDERS_QUERY_KEY = 'work-orders'

export const ACTION_CONFIG = 'config'
const ACTION_GENERAL_CONFIG = 'general-end-config'
const ACTION_DATED_DETAILS = 'dated-details'
const ACTION_REVISION_DETAILS = 'revision-details'
export const ACTION_REVISION_SUMMARY = 'revision-summary'
const ACTION_REVISION_PREVIEW = 'revision-preview'
export const CURRENT = 'current'

// vendor WO mod reject settings
// corresponds with general_schema.yaml's modification's vendor_work_order_mod_rejection_reason enum
export const LIST = 'list'
export const FREE_FORM = 'free_form' // default
export const BOTH = 'both'

const endWorkOrder = (workOrderId: number, request: any) =>
  fetch.post(`/api/v2/work-orders/${workOrderId}/end/`, request)
const modifyWorkOrder = (workOrderId: number, request: WorkOrderRevisionRequest): Promise<WorkOrderRevisionRequest> =>
  fetch.post(`/api/v2/work-orders/${workOrderId}/modify/`, request)
const modifyWorkOrderPreview = (
  workOrderId: number,
  request: WorkOrderRevisionRequest
): Promise<WorkOrderRevisionSummary> => fetch.post(`/api/v2/work-orders/${workOrderId}/modify/preview/`, request)
const bulkModifyWorkOrderPreview = (
  workOrderId: string,
  request: WorkOrderRevisionRequest
): Promise<WorkOrderRevisionSummary> => fetch.post(`/api/v2/work-orders/${workOrderId}/bulk-modify/preview/`, request)
const reopenWorkOrder = (workOrderId: number, request: any) =>
  fetch.post(`/api/v2/work-orders/${workOrderId}/reopen/`, request)
const reapplyWorkOrder = (workOrderId: number, request: any): Promise<any> =>
  fetch.post(`/api/v2/work-orders/${workOrderId}/reapply/`, request)
const list = (urlParams: WorkOrderListQueryParams): Promise<WorkOrderListResponse> => {
  return fetch.get(`/api/v2/work-orders/?${qs.stringify(urlParams, { arrayFormat: 'comma', skipNulls: true })}`)
}
const bulk_eligible_ids = (urlParams: Record<string, any>): Promise<number[]> => {
  return fetch.get(
    `/api/v2/work-orders/bulk_eligible_ids?${qs.stringify(urlParams, { arrayFormat: 'comma', skipNulls: true })}`
  )
}
const getWorkOrder = (workOrderId: number): Promise<WorkOrderDetail> => fetch.get(`/api/v2/work-orders/${workOrderId}`)
const getCurrentWorkOrder = (workOrderId: number | undefined): Promise<WorkOrderCurrentData> =>
  fetch.get(`/api/v2/work-orders/${workOrderId}/current/`)
const cancelWorkOrder = (workOrderId: number, request: any) =>
  fetch.post(`/api/v2/work-orders/${workOrderId}/cancel/`, request)
const onboardWorkOrder = (workOrderId: number, request: any): Promise<WorkOrderOnboardResult> =>
  fetch.post(`/api/v2/work-orders/${workOrderId}/onboard`, request)

const getWorkOrderDatedDetails = (workOrderId: number): Promise<WorkOrderDatedDetails[]> =>
  fetch.get(`/api/v2/work-orders/${workOrderId}/${ACTION_DATED_DETAILS}/`)

const getSingleWorkOrderDatedDetails = (workOrderId: number, forDate: DateString): Promise<WorkOrderDatedDetails> =>
  fetch.get(`/api/v2/work-orders/${workOrderId}/${ACTION_DATED_DETAILS}/?for_date=${forDate}`)

const getWorkOrderRevisionsData = (workOrderId: number): Promise<WorkOrderRevisionsData[]> =>
  fetch.get(`/api/v2/work-orders/${workOrderId}/${ACTION_REVISION_DETAILS}/`)

const getWorkOrderRevisionSummary = (workOrderId: number): Promise<WorkOrderRevisionSummary> =>
  fetch.get(`/api/v2/work-orders/${workOrderId}/${ACTION_REVISION_SUMMARY}/`)

const getWorkOrderConfig = (workOrderId: number): Promise<WorkOrderConfigResponse> =>
  fetch.get(`/api/v2/work-orders/${workOrderId}/${ACTION_CONFIG}/`)

const getWorkOrderGeneralConfig = (urlParams = {}): Promise<WorkOrderGeneralConfigResponse> =>
  fetch.get(`/api/v2/work-orders/config/?${qs.stringify(urlParams)}`)
const getWorkOrderBudgetHistory = (workOrderId: number): Promise<BudgetHistoryRecord[]> =>
  fetch.get(`/api/v2/work-orders/${workOrderId}/budget-history/`)
const getWorkOrderWorkTypes = (workOrderId: number): Promise<WorkTypeRecord[]> =>
  fetch.get(`/api/v2/work-orders/${workOrderId}/work-types/`)
const getWorkOrderShiftStrategy = (workOrderId: number): Promise<ShiftStrategyRecord> =>
  fetch.get(`/api/v2/work-orders/${workOrderId}/shift-strategy/`)
const getWorkOrderShiftDetails = (workOrderId: number): Promise<ShiftDetails> =>
  fetch.get(`/api/v2/work-orders/${workOrderId}/shift-details/`)
const getWorkOrderShiftDifferentials = (workOrderId: number): Promise<ShiftDifferential[]> =>
  fetch.get(`/api/v2/work-orders/${workOrderId}/shift-differentials/`)
const getCandidateDetails = (workOrderId: number): Promise<CandidateWorkerDetails> =>
  fetch.get(`/api/v2/work-orders/${workOrderId}/candidate-details/`)

const getWorkOrderWorkTypesByWorkTypeProfile = (
  workOrderId: number,
  workTypeProfileId: number,
  effectiveDate: string
): Promise<WorkTypeRecord[]> =>
  fetch.get(
    `/api/v2/work-orders/${workOrderId}/work-types/?effective_date=${effectiveDate}&work_type_profile=${workTypeProfileId}`
  )

const getWorkTypesByWorkTypeProfile = (workTypeProfileId: number): Promise<WorkTypeForProfileRecord[]> =>
  fetch.get(`/api/v2/work_type_profiles/${workTypeProfileId}/work-types/`)

const deleteRevision = (workOrderId: number, pullRequestId?: number) =>
  fetch.post(`/api/v2/work-orders/${workOrderId}/cancel-pending-revision`, { pull_request_id: pullRequestId })
const cancelEnd = (workOrderId: number) => fetch.post(`/api/v2/work-orders/${workOrderId}/cancel-end/`, {})
const getApproversForRevision = (workOrderId: number | undefined, request: any): Promise<WorkflowApproverResponse> =>
  fetch.post(`/api/v2/work-orders/${workOrderId}/modify/approvers`, request)
const getApproversForReopen = (workOrderId: number | undefined, request: any) =>
  fetch.post(`/api/v2/work-orders/${workOrderId}/reopen/approvers`, request)
const getApproversForEnd = (workOrderId: number | undefined, request: any) =>
  fetch.post(`/api/v2/work-orders/${workOrderId}/end/approvers`, request)

const offer = (workOrderId: number, data: JobApplicationOfferPayload): Promise<void> =>
  fetch.post(`/api/v2/work-orders/${workOrderId}/offer`, data)

const offerDecline = (workOrderId: number, data: RejectOfferInput): Promise<void> =>
  fetch.put(`/api/v2/work-orders/${workOrderId}/decline/`, data)

const bulkOnboardUpdatedValue = (data: BulkOnboardWorkOrdersPayload): Promise<BulkOnboardUpdatedValuesResponse> =>
  fetch.post('/api/v2/work-orders-bulk-onboard/updated-values', data)

const bulkOnboardCandidatesDetails = (
  data: BulkOnboardWorkOrdersPayload,
  filters: BulkOnboardCandidatesFilters
): Promise<BulkOnboardCandidateDetailsResponse> =>
  fetch.post(`/api/v2/work-orders-bulk-onboard/candidates?${qs.stringify(filters)}`, data)

const bulkOnboard = (data: BulkOnboardWorkOrdersPayload): Promise<BulkOnboardResponse> =>
  fetch.post('/api/v2/work-orders-bulk-onboard/bulk-onboard/', data)

const bulkOnboardValidate = (data: BulkOnboardWorkOrdersPayload): Promise<void> =>
  fetch.post('/api/v2/work-orders-bulk-onboard/validate/', data)

const getActivity = (workOrderId: number, params: Partial<OffsetLimit>): Promise<OffsetLimitPaginationResponse<Log>> =>
  fetch.get(`/api/v2/work-orders/${workOrderId}/activity/${qs.stringify(params, { addQueryPrefix: true })}`)

// Comments API
const getComments = (
  workOrderId: number,
  params: Partial<OffsetLimit>
): Promise<OffsetLimitPaginationResponse<Comment>> =>
  fetch.get(`/api/v2/work-orders/${workOrderId}/comments/${qs.stringify(params, { addQueryPrefix: true })}`)

const deleteComment = (workOrderId: number, commentId: number) =>
  fetch.delete(`/api/v2/work-orders/${workOrderId}/comments/${commentId}/`)

const createComment = (workOrderId: number, comment: CommentFormData) =>
  fetch.post(`/api/v2/work-orders/${workOrderId}/comments/`, comment)

const updateComment = (workOrderId: number, commentId: number, comment: CommentFormData) =>
  fetch.put(`/api/v2/work-orders/${workOrderId}/comments/${commentId}/`, comment)

// Attachments API
const getAttachments = (
  workOrderId: number,
  params: Partial<OffsetLimit>
): Promise<OffsetLimitPaginationResponse<WorkOrderAttachment>> =>
  fetch.get(`/api/v2/work-orders/${workOrderId}/attachments/${qs.stringify(params, { addQueryPrefix: true })}`)

const deleteAttachment = (workOrderId: number, attachmentId: number) =>
  fetch.delete(`/api/v2/work-orders/${workOrderId}/attachments/${attachmentId}/`)

const createAttachment = (workOrderId: number, data: FormData) =>
  fetch.postFile(`/api/v2/work-orders/${workOrderId}/attachments/`, data, { noContentType: true })

const updateAttachment = (workOrderId: number, attachmentId: number, data: FormData) =>
  fetch.put(`/api/v2/work-orders/${workOrderId}/attachments/${attachmentId}/`, data)

const uploadBulkWorkOrderUpdateFile = (formData: FormData): Promise<void> => {
  return fetch.postFile('/api/bulk_upload_work_order_updates/', formData, { noContentType: true })
}

type WorkOrderListResponse = OffsetLimitPaginationResponse<WorkOrder>
export const workOrderApi = {
  list,
  bulk_eligible_ids,
  getWorkOrder,
  getCurrentWorkOrder,
  getWorkOrderDatedDetails,
  getSingleWorkOrderDatedDetails,
  getWorkOrderGeneralConfig,
  getApproversForRevision,
  getApproversForReopen,
  getApproversForEnd,
  deleteRevision,
  cancelEnd,
  getWorkOrderConfig,
  onboardWorkOrder,
  reopenWorkOrder,
  reapplyWorkOrder,
  modifyWorkOrder,
  modifyWorkOrderPreview,
  bulkModifyWorkOrderPreview,
  offer,
  offerDecline,
  bulkOnboard,
  bulkOnboardUpdatedValue,
  bulkOnboardCandidatesDetails,
  bulkOnboardValidate,
  getActivity,
  comments: {
    list: getComments,
    create: createComment,
    delete: deleteComment,
    update: updateComment
  },
  attachments: {
    list: getAttachments,
    create: createAttachment,
    delete: deleteAttachment,
    update: updateAttachment
  }
}

export const workOrderListQueryOpts = (queryParams: WorkOrderListQueryParams, enabled: boolean = true) => ({
  queryKey: [WORK_ORDERS_QUERY_KEY, 'list', queryParams],
  queryFn: () => workOrderApi.list(queryParams as WorkOrderListQueryParams),
  keepPreviousData: true,
  enabled
})
export const bulkEligibleWorkOrderIdsOpts = (queryParams: Record<string, any>) => ({
  queryKey: [WORK_ORDERS_QUERY_KEY, 'bulk_eligible_ids', queryParams],
  queryFn: () => workOrderApi.bulk_eligible_ids(queryParams)
})
export const useWorkOrderQuery = (id: number | undefined) =>
  useQuery({
    queryKey: [WORK_ORDERS_QUERY_KEY, id],
    queryFn: () => workOrderApi.getWorkOrder(id!),
    enabled: Number.isInteger(id),
    staleTime: 1000 * 60 * 5 // Five Minutes
  })

export const useWorkOrderCurrentQuery = (id: number | undefined) =>
  useQuery({
    queryKey: [WORK_ORDERS_QUERY_KEY, id, CURRENT],
    queryFn: () => workOrderApi.getCurrentWorkOrder(id!),
    enabled: Number.isInteger(id)
  })

export const useWorkOrderConfigQuery = (id: number | undefined) =>
  useQuery({
    queryKey: [WORK_ORDERS_QUERY_KEY, id!, ACTION_CONFIG],
    queryFn: () => workOrderApi.getWorkOrderConfig(id!),
    enabled: Boolean(id),
    staleTime: 1000 * 60 * 5 // Five Minutes
  })

export const useWorkOrderRevisionSummaryQuery = (id?: number) =>
  useQuery({
    queryKey: [WORK_ORDERS_QUERY_KEY, id, ACTION_REVISION_SUMMARY],
    queryFn: () => getWorkOrderRevisionSummary(id!),
    enabled: Number.isInteger(id)
  })

export const useWorkOrderBudgetHistoryQuery = (id: number, opts: UseQueryOptions<BudgetHistoryRecord[]>) =>
  useQuery({
    queryKey: [WORK_ORDERS_QUERY_KEY, id, 'budget-history'],
    queryFn: () => getWorkOrderBudgetHistory(id),
    enabled: Number.isInteger(id) && ifDefinedElse(opts.enabled, true),
    ...opts
  })

export const useWorkOrderWorkTypesQuery = (id: number) =>
  useQuery({
    queryKey: [WORK_ORDERS_QUERY_KEY, id, 'work-types'],
    queryFn: () => getWorkOrderWorkTypes(id),
    enabled: Number.isInteger(id)
  })

export const useWorkOrderShiftStrategyQuery = (id: number) =>
  useQuery({
    queryKey: [WORK_ORDERS_QUERY_KEY, id, 'shift-strategy'],
    queryFn: () => getWorkOrderShiftStrategy(id),
    enabled: Number.isInteger(id)
  })

export const useWorkOrderShiftDetailsQuery = (id: number) =>
  useQuery({
    queryKey: [WORK_ORDERS_QUERY_KEY, id, 'shift-details'],
    queryFn: () => getWorkOrderShiftDetails(id),
    enabled: Number.isInteger(id)
  })

export const useWorkOrderShiftDifferentialsQuery = (id: number) =>
  useQuery({
    queryKey: [WORK_ORDERS_QUERY_KEY, id, 'shift-differentials'],
    queryFn: () => getWorkOrderShiftDifferentials(id),
    enabled: Number.isInteger(id)
  })

export const useWorkOrderCandidateDetailsQuery = (id: number) =>
  useQuery({
    queryKey: [WORK_ORDERS_QUERY_KEY, id, 'candidate-details'],
    queryFn: () => getCandidateDetails(id),
    enabled: Number.isInteger(id)
  })

export const useWorkOrderWorkTypesByWorkTypeProfileQuery = (
  id: number,
  work_type_profile_id: number,
  effective_date: string
) =>
  useQuery({
    queryKey: [WORK_ORDERS_QUERY_KEY, id, 'work-types-work-type-profile', work_type_profile_id, effective_date],
    queryFn: () => getWorkOrderWorkTypesByWorkTypeProfile(id, work_type_profile_id, effective_date),
    enabled: Number.isInteger(id) && effective_date != null
  })

export const useWorkTypesByWorkTypeProfileQuery = (work_type_profile_id: number) =>
  useQuery({
    queryKey: ['work_type_profiles', work_type_profile_id, 'work-types'],
    queryFn: () => getWorkTypesByWorkTypeProfile(work_type_profile_id),
    enabled: Number.isInteger(work_type_profile_id)
  })

export const useWorkOrderPendingRevisionDeleteMutation = (id: number, pullRequestId?: number) =>
  useMutation({
    mutationFn: async () => {
      if (pullRequestId != null) {
        await workOrderApi.deleteRevision(id, pullRequestId)
      }
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries([WORK_ORDERS_QUERY_KEY, id]),
        queryClient.invalidateQueries([APPROVERS_ALL_KEY]), // Reset approvals widget
        queryClient.invalidateQueries([APPROVALS_LIST_QUERY_KEY]) // Reset approvals list page
      ])
    }
  })

export const useWorkOrderReopenMutation = (id: number, opts?: UseMutationOptions<any, unknown, any>) =>
  useMutation({
    mutationFn: data => workOrderApi.reopenWorkOrder(id, data),
    ...opts,
    onSuccess: async (...args) => {
      await Promise.all([
        queryClient.invalidateQueries([WORK_ORDERS_QUERY_KEY, id]),
        queryClient.invalidateQueries([APPROVERS_ALL_KEY]) // Force refresh of approvers cache too
      ])
      if (typeof opts?.onSuccess === 'function') opts.onSuccess(...args)
    }
  })

export const useWorkOrderModifyMutation = (id: number, opts?: UseMutationOptions<any, unknown, any>) =>
  useMutation({
    mutationFn: data => workOrderApi.modifyWorkOrder(id, data),
    ...opts,
    onSuccess: async (...args) => {
      await Promise.all([
        queryClient.invalidateQueries([WORK_ORDERS_QUERY_KEY, id])
        // queryClient.invalidateQueries([APPROVERS_ALL_KEY]) // Force refresh of approvers cache too
      ])
      if (typeof opts?.onSuccess === 'function') opts.onSuccess(...args)
    }
  })

export const useWorkOrderGeneralConfigQuery = (urlParams = {}) =>
  useQuery({
    queryKey: [WORK_ORDERS_QUERY_KEY, urlParams, ACTION_GENERAL_CONFIG],
    queryFn: () => workOrderApi.getWorkOrderGeneralConfig(urlParams),
    staleTime: 1000 * 60 * 5 // Five Minutes
  })

export const useWorkOrderActivityQuery = (id: number, filters?: Partial<OffsetLimit>) => {
  return useInfiniteQuery({
    queryKey: [WORK_ORDERS_QUERY_KEY, id, 'activity-log'],
    queryFn: ({ pageParam = 0 }) => {
      return workOrderApi.getActivity(id, { offset: pageParam, ...filters })
    },
    getNextPageParam: lastPage => lastPage.next_params?.offset ?? false
  })
}

export const useWorkOrderCommentsQuery = (id: number, filters?: Partial<OffsetLimit>) => {
  return useInfiniteQuery({
    queryKey: [WORK_ORDERS_QUERY_KEY, id, 'comments'],
    queryFn: ({ pageParam = 0 }) => {
      return workOrderApi.comments.list(id, { offset: pageParam, ...filters })
    },
    getNextPageParam: lastPage => lastPage.next_params?.offset ?? false,
    staleTime: 1000 * 60 * 5 // Five Minutes
  })
}

export const useWorkOrderCommentsCreateMutation = (
  workOrderId: number,
  opts: UseMutationOptions<void, unknown, CommentFormData> = {}
) =>
  useMutation({
    mutationFn: (comment: CommentFormData) => workOrderApi.comments.create(workOrderId, comment),
    ...opts,
    onSettled: async (...args) => {
      await queryClient.invalidateQueries([WORK_ORDERS_QUERY_KEY, workOrderId, 'comments'])
      if (typeof opts.onSettled === 'function') opts.onSettled(...args)
    }
  })

export const useWorkOrderCommentsDeleteMutation = (
  workOrderId: number,
  opts: UseMutationOptions<void, unknown, number> = {}
) =>
  useMutation({
    mutationFn: (commentId: number) => workOrderApi.comments.delete(workOrderId, commentId),
    ...opts,
    onSettled: async (...args) => {
      await queryClient.invalidateQueries([WORK_ORDERS_QUERY_KEY, workOrderId, 'comments'])
      if (typeof opts.onSettled === 'function') opts.onSettled(...args)
    }
  })

export const useWorkOrderAttachmentQuery = (workOrderId: number, filters?: Partial<OffsetLimit>) =>
  useQuery({
    queryKey: [WORK_ORDERS_QUERY_KEY, workOrderId, 'attachments', filters],
    queryFn: () => workOrderApi.attachments.list(workOrderId, { ...filters }),
    enabled: Number.isInteger(workOrderId)
  })

export const useWorkOrderAttachmentCreateMutation = (
  workOrderId: number,
  opts: UseMutationOptions<void, unknown, FormData> = {}
) =>
  useMutation({
    mutationFn: (data: FormData) => workOrderApi.attachments.create(workOrderId, data),
    ...opts,
    onSettled: async (...args) => {
      await queryClient.invalidateQueries([WORK_ORDERS_QUERY_KEY, workOrderId, 'attachments'])
      if (typeof opts.onSettled === 'function') opts.onSettled(...args)
    }
  })

export const useWorkOrderAttachmentDeleteMutation = (
  workOrderId: number,
  opts: UseMutationOptions<void, unknown, number> = {}
) =>
  useMutation({
    mutationFn: (attachmentId: number) => workOrderApi.attachments.delete(workOrderId, attachmentId),
    ...opts,
    onSettled: async (...args) => {
      await queryClient.invalidateQueries([WORK_ORDERS_QUERY_KEY, workOrderId, 'attachments'])
      if (typeof opts.onSettled === 'function') opts.onSettled(...args)
    }
  })

export const useWorkOrderAttachmentUpdateMutation = (
  workOrderId: number,
  attachmentId: number,
  opts: UseMutationOptions<void, unknown, any> = {}
) =>
  useMutation({
    mutationFn: (data: any) => workOrderApi.attachments.update(workOrderId, attachmentId, data),
    ...opts,
    onSettled: async (...args) => {
      await queryClient.invalidateQueries([WORK_ORDERS_QUERY_KEY, workOrderId, 'attachments'])
      if (typeof opts.onSettled === 'function') opts.onSettled(...args)
    }
  })

export function useVendorVisible() {
  const { data: response, isLoading }: { data: any; isLoading: boolean } = useWorkOrderGeneralConfigQuery()
  return { isVendorVisible: response?.can_view_vendors ?? false, isLoading }
}

export function useContractorBillRateVisible(id: number | undefined | null = null, is_wo = false) {
  const urlParams: any = {}
  if (id !== null) {
    if (is_wo) {
      urlParams.wo_id = id
    } else {
      urlParams.job_id = id
    }
  }
  const { data: response }: { data: any } = useWorkOrderGeneralConfigQuery(urlParams)
  return response?.can_view_contractor_bill_rate ?? false
}

export const useWorkOrderDatedDetailsQuery = (id: number) =>
  useQuery({
    queryKey: [WORK_ORDERS_QUERY_KEY, id, ACTION_DATED_DETAILS],
    queryFn: () => workOrderApi.getWorkOrderDatedDetails(id),
    enabled: Boolean(id)
  })

export const useWorkOrderSingleDateDetailsQuery = (id: number, forDate: DateString) =>
  useQuery({
    queryKey: [WORK_ORDERS_QUERY_KEY, id, ACTION_DATED_DETAILS, forDate],
    queryFn: () => workOrderApi.getSingleWorkOrderDatedDetails(id, forDate),
    enabled: Boolean(id)
  })

export const useWorkOrderRevisionsDataQuery = (id: number) =>
  useQuery({
    queryKey: [WORK_ORDERS_QUERY_KEY, id, ACTION_REVISION_DETAILS],
    queryFn: () => getWorkOrderRevisionsData(id),
    enabled: Boolean(id)
  })

const DEFAULT_USE_RETRY = false
export const useWorkOrderModifyPreviewQuery = (
  workOrderId: number,
  changeSet: WorkOrderRevisionRequest,
  options: { useRetry?: boolean } = {
    useRetry: DEFAULT_USE_RETRY
  }
) =>
  useQuery({
    queryKey: [WORK_ORDERS_QUERY_KEY, workOrderId, ACTION_REVISION_PREVIEW, changeSet],
    queryFn: () => workOrderApi.modifyWorkOrderPreview(workOrderId, changeSet),
    enabled: Number.isInteger(workOrderId),
    retry: options.useRetry ?? DEFAULT_USE_RETRY
  })

export const useWorkOrderModifyBulkPreviewQuery = (
  workOrderId: string,
  changeSet: WorkOrderRevisionRequest,
  options: { useRetry?: boolean } = {
    useRetry: DEFAULT_USE_RETRY
  }
) =>
  useQuery({
    queryKey: [WORK_ORDERS_QUERY_KEY, workOrderId, 'bulk-modify-preview', changeSet],
    queryFn: async () => {
      // Check for invalid work order IDs (0, empty string, or whitespace)
      if (workOrderId === '0' || workOrderId.trim() === '') {
        return Promise.resolve({
          isError: true,
          error: {
            message: `Invalid Work Order ID: "${workOrderId}". Work Order ID cannot be empty.`
          }
        } as Partial<WorkOrderRevisionSummary>)
      }
      return workOrderApi.bulkModifyWorkOrderPreview(workOrderId, changeSet)
    },
    enabled: true,
    retry: options.useRetry ?? DEFAULT_USE_RETRY
  })

export function useVendorModRejectReason(workOrderId: number | undefined) {
  const { data: response }: { data: any } = useWorkOrderConfigQuery(workOrderId)
  return response?.revise?.vendor_wo_mod_rejection_reason ?? FREE_FORM
}

export const useChangePrimaryWorkOrderQuery = (workOrderId: number) =>
  useQuery({
    queryKey: ['change-primary-work-order', workOrderId],
    queryFn: () => fetch.get(`/api/v2/work-order-actions/change-primary/${workOrderId}/form/`)
  })

export const useEndWorkOrderMutation = (
  id: number,
  opts: UseMutationOptions<void, unknown, WorkOrderEndRequest> = {}
) =>
  useMutation({
    mutationFn: (request: WorkOrderEndRequest) => endWorkOrder(id, request),
    ...opts,
    onSettled: async (...args) => {
      await Promise.all([
        queryClient.invalidateQueries([WORK_ORDERS_QUERY_KEY, id]),
        queryClient.invalidateQueries([APPROVERS_ALL_KEY]) // Force refresh of approvers cache too
      ])
      if (typeof opts.onSettled === 'function') opts.onSettled(...args)
    }
  })

export const useCancelWorkOrderMutation = (
  id: number,
  opts: UseMutationOptions<void, unknown, WorkOrderCancelRequest> = {}
) =>
  useMutation({
    mutationFn: (request: WorkOrderCancelRequest) => cancelWorkOrder(id, request),
    ...opts,
    onSettled: async (...args) => {
      await queryClient.invalidateQueries([WORK_ORDERS_QUERY_KEY, id])
      if (typeof opts.onSettled === 'function') opts.onSettled(...args)
    }
  })
type OfferReviseMutationVariables = { id: number; data: JobApplicationOfferPayload }
export const useWOOfferMutation = (opts?: UseMutationOptions<void, unknown, OfferReviseMutationVariables>) => {
  return useMutation({
    mutationFn: ({ id, data }: OfferReviseMutationVariables) => workOrderApi.offer(id, data),
    ...opts,
    onSuccess(...args) {
      opts?.onSuccess?.(...args)
      invalidateJobApplicationQueries()
    }
  })
}

export type BulkOnboardCandidatesFilters = Partial<OffsetLimit> & {
  search?: string
}

export type BulkOnboardUpdatedValue = {
  previous_value: string
  new_value: string
}

export type BulkOnboardCandidateDetails = {
  work_order_id: PrimaryKey
  applicant_name: string
  vendor: string
  fields_values: Record<BulkOnboardFieldNames | string, BulkOnboardUpdatedValue>
}

type BulkOnboardCandidateDetailsResponse = OffsetLimitPaginationResponse<BulkOnboardCandidateDetails>

type WorkOrderListQueryParams = QueryParams<typeof filtersDef, typeof actionBarDef>

export type BulkOnboardUpdatedValuesResponse = {
  new_values: Record<BulkOnboardFieldNames | string, string>
}

export enum BulkOnboardFieldNames {
  additional_managers = 'additional_managers',
  // connector_details_section = 'connector_details_section',
  duration = 'duration',
  end_date = 'end_date',
  fee_profile = 'fee_profile',
  fee_profile_dummy = 'fee_profile_dummy',
  fee_structure_profile = 'fee_structure_profile',
  job_location = 'job_location',
  offered_bill_rate = 'offered_bill_rate',
  start_date = 'start_date',
  authentication_method = 'authentication_method'
}

// 'set' = update the value for all row
// 'default' = set a default value for rows without a previous value
export type BulkOnboardFieldUpdateType = 'set' | 'default'

export type BulkOnboardFieldValue = {
  field_id: BulkOnboardFieldNames | string
  value: string | number | boolean | number[] | Record<string, any> | null
  update_type: BulkOnboardFieldUpdateType
}

export const useBulkOnboardUpdatedValues = (data: BulkOnboardWorkOrdersPayload) =>
  useQuery({
    queryKey: [WORK_ORDERS_QUERY_KEY, 'bulk-onboard', 'updated-values', { data }],
    queryFn: () => workOrderApi.bulkOnboardUpdatedValue(data)
  })

export const useBulkOnboardCandidatesDetails = (
  data: BulkOnboardWorkOrdersPayload,
  filters: BulkOnboardCandidatesFilters
) =>
  useQuery({
    queryKey: [WORK_ORDERS_QUERY_KEY, 'bulk-onboard', 'candidates', { data, filters }],
    queryFn: () => workOrderApi.bulkOnboardCandidatesDetails(data, filters)
  })

export interface BulkOnboardWorkOrdersPayload {
  job?: PrimaryKey
  work_order_ids?: PrimaryKey[]
  fields?: BulkOnboardFieldValue[]
}

interface BulkOnboardResponse {
  task_id: number
  bulk_update_record_id: number
}
type BulkOnboardMutationVariables = { data: BulkOnboardWorkOrdersPayload }
export const useBulkOnboardMutation = (
  opts?: UseMutationOptions<BulkOnboardResponse, unknown, BulkOnboardMutationVariables>
) => {
  return useMutation({
    mutationFn: ({ data }: BulkOnboardMutationVariables) => workOrderApi.bulkOnboard(data),
    ...opts
  })
}

export const useBulkOnboardValidateMutation = (
  opts?: UseMutationOptions<void, unknown, BulkOnboardMutationVariables>
) => {
  return useMutation({
    mutationFn: ({ data }: BulkOnboardMutationVariables) => workOrderApi.bulkOnboardValidate(data),
    ...opts
  })
}

export enum ExpenseTypeEnum {
  OPERATIONAL = 1,
  CAPITAL = 2
}

export const EXPENSE_TYPE_DISPLAY = {
  [ExpenseTypeEnum.OPERATIONAL]: 'term.workorder.field.expense_type.value_operational',
  [ExpenseTypeEnum.CAPITAL]: 'term.workorder.field.expense_type.value_capital'
}

export enum SourceTypeEnum {
  SUPPLIER_SOURCED = 1,
  PAYROLLED = 2,
  DIRECTED = 3,
  DIRECT_HIRE = 5,
  SOW = 6,
  WP = 7
}

export const SOURCE_TYPE_DISPLAY = {
  [SourceTypeEnum.SUPPLIER_SOURCED]: 'term.workorder.field.source_type.value_supplier_sourced',
  [SourceTypeEnum.PAYROLLED]: 'term.workorder.field.source_type.value_payrolled',
  [SourceTypeEnum.DIRECTED]: 'term.workorder.field.source_type.value_directed',
  [SourceTypeEnum.DIRECT_HIRE]: 'term.workorder.field.source_type.value_direct_hire',
  [SourceTypeEnum.SOW]: 'term.workorder.field.source_type.value_sow',
  [SourceTypeEnum.WP]: 'term.worker_profile'
}

export enum ModuleEnum {
  CONTINGENT = 0,
  SOW = 1,
  WPM = 2
}

export enum WorkOrderTabs {
  OVERVIEW = 0,
  CHECKLISTS,
  SHIFTS,
  EXPENSES
}

const WORK_ORDER_APPROVAL_FIELD_SETS_QUERY_KEY = 'work-order-approval-fieldsets'

const updateWorkOrderApprovalFieldSet = (fieldSet: WorkOrderApprovalFieldSet): Promise<WorkOrderApprovalFieldSet> => {
  if (fieldSet.id) {
    return fetch.put(`/api/v2/work-order-approval-fieldsets/${fieldSet.id}`, fieldSet)
  }
  return fetch.post(`/api/v2/work-order-approval-fieldsets/`, fieldSet)
}

export const workOrderFieldSetApi = {
  getContractFields: (): Promise<ContractField[]> => fetch.get('/api/v2/work-orders/contract-fields/'),
  // Cheating and not supporting actual pagination even though the API itself does.  Who is making 100+ of these???
  get: (): Promise<OffsetLimitPaginationResponse<WorkOrderApprovalFieldSet>> =>
    fetch.get(`/api/v2/work-order-approval-fieldsets/`),
  update: updateWorkOrderApprovalFieldSet,
  delete: (id: number) => fetch.delete(`/api/v2/work-order-approval-fieldsets/${id}/`),
  activityLogs: (): Promise<Log[]> => fetch.get(`/api/v2/work-order-approval-fieldsets/activity/`)
}

export const useWorkOrderApprovalFieldSetsQuery = () =>
  useQuery({ queryKey: [WORK_ORDER_APPROVAL_FIELD_SETS_QUERY_KEY], queryFn: workOrderFieldSetApi.get })

export const useContractFieldsQuery = () =>
  useQuery({ queryKey: ['work-order-contract-fields'], queryFn: workOrderFieldSetApi.getContractFields })

export const useUpdateWorkOrderFieldSetMutation = (
  opts: UseMutationOptions<WorkOrderApprovalFieldSet, unknown, WorkOrderApprovalFieldSet> = {}
) => {
  return useMutation({
    mutationFn: data => workOrderFieldSetApi.update(data),
    ...opts,
    onSuccess: async () => {
      await queryClient.invalidateQueries([WORK_ORDER_APPROVAL_FIELD_SETS_QUERY_KEY])
    }
  })
}

export const useDeleteWorkOrderFieldSetMutation = () => {
  return useMutation({
    mutationFn: workOrderFieldSetApi.delete,
    onSuccess: async () => {
      await queryClient.invalidateQueries([WORK_ORDER_APPROVAL_FIELD_SETS_QUERY_KEY])
    }
  })
}

export const useWorkOrderApprovalFieldSetsActivityQuery = () =>
  useQuery({
    queryKey: [WORK_ORDER_APPROVAL_FIELD_SETS_QUERY_KEY, 'activityLogs'],
    queryFn: workOrderFieldSetApi.activityLogs
  })

export type WorkerProfileWorkOrderParams = Partial<{
  business_unit: number | null
  budget_allocations: WorkOrderBudgetAllocations[]
  vendor?: number
  candidate?: number
  job_template: number | null
  job_template_job_type: number | null
  reason_for_hire: number | null
  provisioning_types: number[]
  provisioning_eligible: boolean
  location: number | null
  start_date: string | null
  end_date: string | null
  resource_manager: number | null
  program_team_entity: number | null
  program_team: number[]
  generic_fields: Record<string, string>
  candidate_generic_fields: Record<string, string>
  charge_codes: PrimaryKey[]
  work_week: number | null
  comment?: { comment: string; external: boolean }
  create_as_draft?: boolean
  connector_data: {
    workday?: WorkOrderWorkdayData | null
  }
}>

// TODO (vic) - refactor all of this to remove redundant redefinition of a work order for "WPM"
export type WorkerProfileWorkOrder = {
  id: number
  business_unit: WorkOrderNestedData<number>
  candidate: WorkOrderNestedData<number>
  job_template: WorkOrderNestedData<number>
  job_template_job_type: WorkOrderNestedData<number>
  reason_for_hire: WorkOrderNestedData<number>
  reason_for_hire_text: WorkOrderNestedData<number>
  person_being_back_filled: WorkOrderNestedData<string>
  provisioning_types: WorkOrderNestedDataArray<number>
  provisioning_eligible: WorkOrderNestedData<boolean>
  location: WorkOrderNestedData<number>
  start_date: string
  end_date: string
  end_time: WorkOrderNestedData<string>
  end_timezone: WorkOrderNestedData<string>
  resource_manager: WorkOrderNestedData<number>
  program_team: WorkOrderNestedDataArray<number>
  program_team_entity: WorkOrderNestedData<number>
  generic_fields: Record<string, any>
  charge_codes: WorkOrderNestedDataArray<ChargeCodeRawValues>
  work_week: WorkOrderNestedData<number>
  status: WorkOrderNestedData<number>
  workday: WorkOrderWorkdayData | null
  vendor: WorkOrderNestedData<string>
  modification_status_message_key: string | null
}

export type FormWorkOrder = Partial<WorkerProfileWorkOrder>

export type ContractorData = Partial<{
  generic_fields: Record<string, any>
}>

export type WorkerProfileContributor = {
  role: string
  updated_at: string | null
  marked_done: boolean
}

export type WorkerProfile = {
  work_order: WorkerProfileWorkOrder
  revision_indicator: string
  contributors: WorkerProfileContributor[]
}

export const useCreateWPMWorkOrderMutation = () => {
  const { mutateAsync, isLoading } = useMutation({
    mutationFn: (params: WorkerProfileWorkOrderParams): Promise<WorkOrderDetail> => {
      return fetch.post('/api/v2/worker-profiles', params)
    }
  })

  return {
    createWorkOrder: (params: WorkerProfileWorkOrderParams) => {
      return mutateAsync(params)
    },
    isLoading
  }
}

export const useUpdateWorkerProfileMutation = () => {
  const { mutateAsync, isLoading } = useMutation({
    mutationFn: async ({
      url,
      workerProfile,
      params
    }: {
      url: string
      workerProfile: WorkerProfile
      params: WorkerProfileWorkOrderParams
    }): Promise<void> => {
      const result = await fetch.put(url, {
        ...params,
        revision_indicator: workerProfile.revision_indicator
      })

      queryClient.invalidateQueries([WORK_ORDERS_QUERY_KEY, workerProfile.work_order.id])
      queryClient.invalidateQueries({ queryKey: ['checklist-actions'] })

      return result
    }
  })

  return {
    saveProgress: (workerProfile: WorkerProfile, params: WorkerProfileWorkOrderParams) => {
      return mutateAsync({ url: `/api/v2/worker-profiles/${workerProfile.work_order?.id}`, workerProfile, params })
    },
    submitProgress: (workerProfile: WorkerProfile, params: WorkerProfileWorkOrderParams) => {
      return mutateAsync({
        url: `/api/v2/worker-profiles/${workerProfile.work_order?.id}/submit`,
        workerProfile,
        params
      })
    },
    isLoading
  }
}

/**
 * Mutation for onboarding a WPM work order - supports onSuccess callback so you can do things like nav to active WO
 * @param id
 * @param opts
 */
export const useOnboardWorkerProfileMutation = (id: number, opts?: UseMutationOptions<any, unknown, any>) => {
  const { mutateAsync, isLoading } = useMutation({
    mutationFn: async ({ data, workerProfile }: { data: WorkerProfileWorkOrderParams; workerProfile: WorkerProfile }) =>
      fetch.post(`/api/v2/worker-profiles/${id}/onboard`, {
        ...data,
        revision_indicator: workerProfile.revision_indicator
      }),
    onSuccess: async (...args) => {
      await queryClient.invalidateQueries([WORK_ORDERS_QUERY_KEY, id])
      if (typeof opts?.onSuccess === 'function') opts.onSuccess(...args)
    }
  })

  return {
    onboard: (workerProfile: WorkerProfile, data: WorkerProfileWorkOrderParams) => {
      return mutateAsync({ data, workerProfile })
    },
    isLoading
  }
}

export const useReturnToDraftMutation = (id: number, opts?: UseMutationOptions<any, unknown, any>) => {
  const { mutateAsync, isLoading } = useMutation({
    mutationFn: async ({ comment }: { comment?: CommentFormData }) =>
      fetch.post(`/api/v2/worker-profiles/${id}/return-to-draft`, { comment }),
    onSuccess: async (...args) => {
      await queryClient.invalidateQueries([WORK_ORDERS_QUERY_KEY, id])
      await queryClient.invalidateQueries({ queryKey: ['checklist-actions'] })
      if (typeof opts?.onSuccess === 'function') opts.onSuccess(...args)
    }
  })

  return {
    returnToDraft: (comment: CommentFormData | undefined) => {
      return mutateAsync({ comment })
    },
    isLoading
  }
}

/**
 * Mutation for deleting a WPM work order
 *
 * @remarks For now this is soft deletion
 * TODO (Vic) - migrate this to POST operation so reason/comments can be in request body
 *
 */
export const useDeleteWorkerProfileMutation = (id: number) => {
  const { mutateAsync, isLoading } = useMutation({
    mutationFn: ({ reason, comments }: { reason: number; comments: string }) => {
      return fetch.delete(`/api/v2/worker-profiles/${id}?reason=${reason}&comments=${comments}`)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries([WORK_ORDERS_QUERY_KEY, id])
    }
  })

  return {
    deleteWorkerProfile: (reason: number, comments: string) => {
      return mutateAsync({ reason, comments })
    },
    isLoading
  }
}

// TODO (vic) - more unnecessary re-shaping of data to make WPM be proprietary - refactor
const convertCurrentWorkOrderToWorkerProfile = (data: WorkOrderCurrentData): WorkerProfile['work_order'] => {
  const latestEDF = closestEdfOn(data.effective_dated_fields, data.closest_range_key)!

  return {
    id: data.id,
    business_unit: latestEDF.organization_unit,
    candidate: data.candidate!,
    job_template: latestEDF.job_template,
    job_template_job_type: latestEDF.job_template_job_type,
    reason_for_hire: data.reason_for_hire,
    reason_for_hire_text: data.reason_for_hire_text,
    person_being_back_filled: data.person_being_back_filled,
    provisioning_types: data.provisioning_types,
    provisioning_eligible: data.provisioning_eligible,
    location: latestEDF.location,
    start_date: data.start_date.raw_form_value,
    end_date: data.end_date.raw_form_value,
    end_time: data.end_time,
    end_timezone: data.end_timezone,
    resource_manager: latestEDF.resource_manager,
    program_team_entity: data.program_team_entity,
    program_team: latestEDF.program_team!,
    generic_fields: latestEDF.generic_fields ?? {},
    charge_codes: latestEDF.charge_codes ?? [],
    work_week: latestEDF.work_week,
    status: data.status,
    workday: data.workday,
    vendor: data.vendor,
    modification_status_message_key: data.modification_status_message_key
  }
}

export type WorkerProfileShowResponse = {
  work_order: WorkOrderCurrentData
  connector_data: { workday?: { position?: PrimaryKey } }
  contractor_data: ContractorData
  contributors: WorkerProfileContributor[]
}

export const useWorkerProfile = (id: number) => {
  const {
    data,
    isFetching,
    isError,
    isInitialLoading,
    refetch: refetchWorkOrder
  } = useQuery<WorkerProfileShowResponse>({
    queryKey: [WORK_ORDERS_QUERY_KEY, id, 'wpm-alternate-body'],
    queryFn: () => fetch.get(`/api/v2/worker-profiles/${id}`)
  })

  const workerProfile = useMemo(() => {
    if (data) {
      return {
        ...data,
        work_order: convertCurrentWorkOrderToWorkerProfile(data.work_order),
        revision_indicator: data.work_order.revision_indicator,
        connector_data: {
          workday: data.work_order.workday
        }
      }
    }
    return undefined
  }, [data])

  return {
    workerProfile,
    refetch: () => {
      return refetchWorkOrder()
    },
    isError,
    isInitialLoading,
    isLoading: isFetching
  }
}

export type WorkerCollaborationValidationStatus = {
  is_valid: boolean
  force_error?: boolean
  details?: any // DRF shape, how to type this :hmm:
  banner_alert?: BannerAlertInformation
}

export type BannerAlertInformation = {
  title_id: string
  message_id: string
  alert_type: 'warning' | 'error' | 'info' | 'success'
}

export type WorkerProfileFormConfig = {
  is_outbound_provisioning_enabled: boolean
  is_provisioning_eligible_default: boolean
  has_program_team_entity_rules?: boolean
  can_override_auto_assign_program_entity?: boolean
  actions: {
    save_progress: WorkOrderActionDirection
    onboard: WorkOrderActionDirection
    delete: WorkOrderActionDirection
    return_to_draft: WorkOrderActionDirection
  }
  contractor_generic_form: string
  can_create_candidate: boolean
  charge_code_config: ChargeCodesConfigValues
  charge_code_help_text: string | null
  default_location_from_rm: boolean
  show_connector_details: boolean
  field_permissions: {
    end_date: {
      view: boolean
      edit: boolean
    }
    end_time: {
      view: boolean
      edit: boolean
    }
    end_timezone: {
      view: boolean
      edit: boolean
    }
    charge_codes: {
      view: boolean
      edit: boolean
    }
    job_template: {
      view: boolean
      edit: boolean
    }
    location: {
      view: boolean
      edit: boolean
    }
    business_unit: {
      view: boolean
      edit: boolean
    }
    positions: {
      view: boolean
      edit: boolean
    }
    program_team: {
      view: boolean
      edit: boolean
    }
    program_team_entity: {
      view: boolean
      edit: boolean
      required: boolean
    }
    provisioning_eligible: {
      view: boolean
      edit: boolean
    }
    provisioning_types: {
      view: boolean
      edit: boolean
    }
    reason_for_hire: {
      view: boolean
      edit: boolean
    }
    resource_manager: {
      view: boolean
      edit: boolean
    }
    start_date: {
      view: boolean
      edit: boolean
    }
    work_week: {
      view: boolean
      edit: boolean
    }
  }
  defaults: {
    end_time?: string
    end_timezone?: string
  }
  validation_status: WorkerCollaborationValidationStatus
}

export const useWorkerProfileFormConfig = (id?: number) =>
  useQuery({
    queryKey: [WORK_ORDERS_QUERY_KEY, id, 'wpm-config'],
    queryFn: (): Promise<WorkerProfileFormConfig> => {
      const url = id ? `/api/v2/worker-profiles/${id}/config` : '/api/v2/worker-profiles/config'
      return fetch.get(url)
    },
    staleTime: 1000 * 60 * 5 // Five Minutes
  })

export const useCandidateGenericFieldsQuery = (candidate_id: number, enabled: boolean) =>
  useQuery({
    queryKey: [WORK_ORDERS_QUERY_KEY, 'candidate-generic-fields', candidate_id],
    queryFn: () => fetch.get(`/api/v2/work-orders/candidate/${candidate_id}/generic-fields/`),
    enabled: !!candidate_id && enabled
  })

type RejectOfferInput = Partial<{
  // This is all the things that the job_application PUT endpoint can take
  // but you won't need to provide all of these, just the ones you are updating.
  reject_reason: string
}>

export const useDeclineOfferMutation = (
  opts?: UseMutationOptions<void, unknown, { workOrderId: number } & RejectOfferInput>
) =>
  useMutation({
    mutationFn: ({ workOrderId, ...data }: { workOrderId: number } & RejectOfferInput) =>
      workOrderApi.offerDecline(workOrderId, data),
    ...opts,
    onSuccess(...args) {
      opts?.onSuccess?.(...args)
      invalidateJobApplicationQueries()
    }
  })

export const useBulkUpdateWorkOrderByFile = (opts?: UseMutationOptions<void, unknown, any>) =>
  useMutation({
    mutationFn: uploadBulkWorkOrderUpdateFile,
    ...opts,
    onSuccess: async (...args) => {
      if (typeof opts?.onSuccess === 'function') opts.onSuccess(...args)
    }
  })

export type RateSchedulePreviewRequest = {
  job?: number
  candidate?: number
  // Only relevant alongside job/candidate - used to determine the candidate's source type.
  is_pre_identified?: boolean
  work_order?: number
  pay_rate: string
  markup: string
  start_date: string
  end_date: string
  calculation_pack: number | null | undefined
  // any other field that would be relevant for rate card matching and is selectable during apply/offer/onboard flows
  work_site: number | null
  worker_classification: number | null
  source_type: number | null
  // When we want to only run the preview for the full period, without guided schedule lookup.
  skip_guided_schedule?: boolean
}

export type RateSchedulePreviewResponse = {
  effective_dated_fields: WorkOrderEffectiveDatedFields[]
  previous_experience_days: number
  guided_rate_schedule: GuidedRateScheduleData | null
  continuous_assignment_name: string | null
  offered_job_title: string | null
  starting_uplift_index: number
  labor_amount: number | null
}

export const getRateSchedulePreview = (data: RateSchedulePreviewRequest): Promise<RateSchedulePreviewResponse> =>
  fetch.post('/api/v2/work-orders/rate-schedule-preview/', data)

export const useRateSchedulePreviewQuery = (
  params: RateSchedulePreviewRequest | undefined,
  opts?: UseQueryOptions<RateSchedulePreviewResponse>
) =>
  useQuery({
    queryKey: ['rate-schedule-preview', params],
    queryFn: () => getRateSchedulePreview(params!),
    enabled:
      (!!params?.work_order || (!!params?.job && !!params?.candidate)) &&
      !!params?.pay_rate &&
      !!params?.markup &&
      !!params?.start_date &&
      !!params?.end_date &&
      !!params?.calculation_pack &&
      (opts?.enabled ?? true),
    staleTime: Infinity,
    keepPreviousData: true,
    ...opts
  })
